import React, { useState } from 'react';
import { Loader2, Wand2 } from 'lucide-react';
import API_ENDPOINTS from '../config/api';
import { useTranslation } from '../hooks/useTranslation.jsx';
import { useNotice } from './DialogProvider';

/**
 * Polish participant text (grammar/clarity) without changing meaning.
 * Pushes a snapshot via onBeforeApply so UndoRedoButtons can revert.
 */
export default function TidyTextButton({
  text,
  instanceId,
  onApply,
  onBeforeApply,
  disabled = false,
  buttonComponent: ButtonComponent = 'button',
  buttonStyle = {},
}) {
  const { t } = useTranslation();
  const notice = useNotice();
  const [tidying, setTidying] = useState(false);
  const trimmed = String(text ?? '').trim();
  const canTidy = trimmed.length > 0 && !disabled && !tidying;

  const handleTidy = async () => {
    if (!canTidy) return;
    onBeforeApply?.(trimmed);
    setTidying(true);
    try {
      const response = await fetch(API_ENDPOINTS.AI_TIDY_TEXT(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: trimmed, instanceId: instanceId || null }),
      });
      if (!response.ok) throw new Error('tidy failed');
      const data = await response.json();
      const tidied = String(data.text ?? '').trim();
      if (!tidied) throw new Error('empty tidy result');
      onApply(tidied);
    } catch (err) {
      console.error('Tidy text failed:', err);
      notice.error(t('formCheck.tidyFailed'));
    } finally {
      setTidying(false);
    }
  };

  return (
    <ButtonComponent
      type="button"
      onClick={handleTidy}
      disabled={!canTidy}
      style={buttonStyle}
      title={t('formCheck.tidyBtn')}
    >
      {tidying ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Wand2 size={13} />}
      {t('formCheck.tidyBtn')}
    </ButtonComponent>
  );
}
